import { useEffect, useState } from 'react';
import { AlertTriangle, History } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { api } from '@/lib/api';
import { customFieldDe, fechaCorta, TIPO_LABEL, type Dispositivo, type OpcionPropiedad, type RolSmartlock } from '@/lib/dispositivos';
import { useCatalogosDispositivos } from './catalogos';

/**
 * Instalar en una casa, o moverlo de una a otra.
 *
 * Antes de confirmar se enseña lo que va a pasar y lo que conviene saber: el
 * historial de incidencias del aparato («este ya ha dado guerra tres veces»)
 * y qué custom field de Hostify se va a escribir. La casilla «sin escribir en
 * Hostify» está para cuando Hostify está caído: deja la instalación como
 * deuda visible, no la esconde.
 */
interface Props {
  abierto: boolean;
  dispositivo: Dispositivo;
  modo: 'instalar' | 'mover';
  onCerrar: () => void;
  onGuardar: (datos: Record<string, unknown>) => Promise<void>;
}

export default function ModalInstalar({ abierto, dispositivo: d, modo, onCerrar, onGuardar }: Props) {
  const { catalogos } = useCatalogosDispositivos();
  const hoy = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
  const [propiedades, setPropiedades] = useState<OpcionPropiedad[]>([]);
  const [listingId, setListingId] = useState('');
  const [deListingId, setDeListingId] = useState('');
  const [principal, setPrincipal] = useState(d.instalaciones.length === 0);
  const [rol, setRol] = useState<RolSmartlock>('smartlock');
  const [fecha, setFecha] = useState(hoy);
  const [nota, setNota] = useState('');
  const [sinHostify, setSinHostify] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;
    api.getDispositivosPropiedades().then(setPropiedades).catch(() => setPropiedades([]));
  }, [abierto]);

  const yaInstaladoEn = new Set(d.instalaciones.map((i) => i.listingId));
  const opciones = propiedades.filter((p) => !yaInstaladoEn.has(p.id));
  const campo = customFieldDe(d.tipo, d.tipo === 'smartlock' ? rol : undefined);
  const ultima = d.resumen.ultimaIncidencia;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!listingId) return setError('Elige la propiedad');
    setGuardando(true);
    setError(null);
    try {
      const comunes = {
        principal: principal || undefined,
        rol: d.tipo === 'smartlock' ? rol : undefined,
        fecha,
        nota: nota.trim() || undefined,
        sinHostify: sinHostify || undefined,
      };
      await onGuardar(modo === 'mover' ? { ...comunes, aListingId: listingId, deListingId: deListingId || undefined } : { ...comunes, listingId });
      onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={abierto} onClose={onCerrar} title={modo === 'mover' ? 'Mover a otra casa' : d.instalaciones.length ? 'Instalar en otra casa más' : 'Instalar en una casa'}>
      <form onSubmit={enviar} className="space-y-4">
        {modo === 'mover' && d.instalaciones.length > 1 && (
          <label className="block">
            <span className="label">Retirar de</span>
            <select className="input mt-1" value={deListingId} onChange={(e) => setDeListingId(e.target.value)}>
              <option value="">Todas las casas ({d.instalaciones.length})</option>
              {d.instalaciones.map((i) => (
                <option key={i.listingId} value={i.listingId}>{i.nickname}</option>
              ))}
            </select>
          </label>
        )}

        <label className="block">
          <span className="label">{modo === 'mover' ? 'Instalar en' : 'Propiedad'} *</span>
          <select className="input mt-1" required value={listingId} onChange={(e) => setListingId(e.target.value)}>
            <option value="">Elige…</option>
            {opciones.map((p) => (
              <option key={p.id} value={p.id}>{p.nickname}</option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label">Fecha</span>
            <input type="date" className="input mt-1" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </label>
          {d.tipo === 'smartlock' && (
            <label className="block">
              <span className="label">Custom field</span>
              <select className="input mt-1" value={rol} onChange={(e) => setRol(e.target.value as RolSmartlock)}>
                <option value="smartlock">Smartlock principal (1236)</option>
                <option value="smartlock2">Segunda smartlock (1840)</option>
              </select>
            </label>
          )}
        </div>

        {(d.instalaciones.length > 0 || modo === 'mover') && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={principal} onChange={(e) => setPrincipal(e.target.checked)} />
            Es donde está físicamente (principal)
          </label>
        )}

        <label className="block">
          <span className="label">Nota</span>
          <textarea className="input mt-1" rows={2} value={nota} onChange={(e) => setNota(e.target.value)} />
        </label>

        {d.resumen.incidencias > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
            <History size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800">
              <p className="font-medium">
                Este aparato lleva {d.resumen.incidencias} incidencia{d.resumen.incidencias === 1 ? '' : 's'}
                {d.resumen.incidencias90d > 0 ? `, ${d.resumen.incidencias90d} en los últimos 90 días` : ''}.
              </p>
              {ultima && (
                <p>Última: {catalogos.incidenciaLabel[ultima.tipo] || ultima.tipo}, el {fechaCorta(ultima.fecha)}.</p>
              )}
            </div>
          </div>
        )}

        <div className="rounded-lg bg-sereno-blue/30 border border-sereno-blue p-3 text-xs text-sereno-dark/70 space-y-1">
          <p className="font-medium text-sereno-dark">Qué va a pasar</p>
          <ul className="list-disc list-inside space-y-0.5">
            {modo === 'mover' && <li>Se retira de {deListingId ? d.instalaciones.find((i) => i.listingId === deListingId)?.nickname : 'todas sus casas'} por cambio de casa.</li>}
            {campo ? (
              <li>Se escribe el custom field <strong>{campo}</strong> de Hostify en el listing y sus canales{modo === 'mover' ? ', y se vacía en el de origen' : ''}.</li>
            ) : (
              <li>{TIPO_LABEL[d.tipo]}: sin custom field en Hostify; solo queda en el inventario.</li>
            )}
            <li>Queda como instalado desde el {fechaCorta(fecha)} y se apunta en su historial.</li>
          </ul>
        </div>

        {campo && (
          <label className="flex items-start gap-2 text-xs text-sereno-dark/60">
            <input type="checkbox" className="mt-0.5" checked={sinHostify} onChange={(e) => setSinHostify(e.target.checked)} />
            <span>
              Sin escribir en Hostify (solo si está caído). La instalación quedará marcada como pendiente hasta que se reintente.
            </span>
          </label>
        )}

        {error && (
          <p className="text-sm text-red-600 flex items-start gap-1.5"><AlertTriangle size={15} className="mt-0.5 flex-shrink-0" /> {error}</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCerrar} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={guardando} className="btn-primary disabled:opacity-50">
            {guardando ? 'Guardando…' : modo === 'mover' ? 'Mover' : 'Instalar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
